import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@stormwater-software/lib-ui';
import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { InspectionContext } from '../../../contexts/InspectionContext';
import findingApi from '../../../utils/api/finding';
import {
  ProjectAsset,
  projectAssetsApi,
} from '../../../utils/api/projectAsset';
import AddFindingModal from '../../Inspections/Findings/AddFindingModal';

type AssetFinding = NonNullable<ProjectAsset['findings']>[number];

interface IAssetFindingsProps {
  projectId: number;
  asset: ProjectAsset;
  /** Present only inside an inspection; enables add/remove. */
  inspectionId?: number;
  /** Editable (write permission + not archived). */
  canEdit: boolean;
  onChanged: () => void;
}

const findingLabel = (f: { type?: string; number?: number; id: number }) =>
  `${f.type || 'Finding'} #${f.number ?? f.id}`;

/**
 * Build the inspection-scoped details URL for a finding, picking the most
 * relevant inspection to land on relative to the one being viewed.
 */
const getFindingsUrl = (finding: AssetFinding, inspectionId?: number) => {
  let inspectionPart: number | undefined;

  if (finding.completed_inspection_id != null) {
    inspectionPart =
      inspectionId &&
      Number(inspectionId) < Number(finding.completed_inspection_id)
        ? inspectionId
        : finding.completed_inspection_id;
  } else {
    inspectionPart =
      inspectionId &&
      Number(inspectionId) > Number(finding.initial_inspection_id)
        ? inspectionId
        : finding.initial_inspection_id;
  }

  return `/inspection/${inspectionPart}/findings/${finding.id}/details`;
};

const AssetFindings: React.FC<IAssetFindingsProps> = ({
  projectId,
  asset,
  inspectionId,
  canEdit,
  onChanged,
}) => {
  const inspectionContext = useContext(InspectionContext);
  const findings = asset.findings || [];
  const manageable = !!inspectionId && canEdit;

  const [showPicker, setShowPicker] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [unlinked, setUnlinked] = useState<AssetFinding[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadUnlinked = async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const all = await findingApi.index(inspectionId);

      setUnlinked((all || []).filter((f: any) => f.project_asset_id == null));
    } catch {
      toast.error('Failed to load findings.');
      setUnlinked([]);
    } finally {
      setLoading(false);
    }
  };

  const openPicker = () => {
    setShowPicker(true);
    loadUnlinked();
  };

  const linkFinding = async (findingId: number) => {
    if (!inspectionId) return;
    setBusyId(findingId);
    const toastId = toast.loading('Adding finding to asset…');

    try {
      await projectAssetsApi.linkFinding(projectId, asset.id, findingId);
      toast.update(toastId, {
        render: 'Finding added to asset.',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setShowPicker(false);
      onChanged();
    } catch {
      toast.update(toastId, {
        render: 'Failed to add finding.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setBusyId(null);
    }
  };

  const unlinkFinding = async (findingId: number) => {
    if (!inspectionId) return;
    setBusyId(findingId);
    const toastId = toast.loading('Removing finding from asset…');

    try {
      await projectAssetsApi.unlinkFinding(projectId, asset.id, findingId);
      toast.update(toastId, {
        render: 'Finding removed from asset.',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      onChanged();
    } catch {
      toast.update(toastId, {
        render: 'Failed to remove finding.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='asset-section__row'>
      <div className='asset-section__col-head'>
        <h5>Findings</h5>
        {manageable && (
          <button
            type='button'
            className='asset-section__finding-add'
            onClick={openPicker}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Finding
          </button>
        )}
      </div>

      {findings.length === 0 ? (
        <p className='asset-section__empty'>
          No findings linked to this asset.
        </p>
      ) : (
        <div className='asset-section__findings'>
          {findings.map((finding) => (
            <span key={finding.id} className='asset-section__finding-chip'>
              <a
                className='asset-section__finding'
                href={getFindingsUrl(finding, inspectionId)}
              >
                {findingLabel(finding)}
              </a>
              {manageable && (
                <button
                  type='button'
                  className='asset-section__finding-remove'
                  aria-label={`Remove ${findingLabel(finding)} from asset`}
                  title='Remove from asset'
                  disabled={busyId === finding.id}
                  onClick={() => unlinkFinding(finding.id)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {showPicker && (
        <Modal
          show={showPicker}
          title={`Add Finding to ${asset.asset_name}`}
          hideButtons
          handleClose={() => setShowPicker(false)}
          handleSubmit={() => setShowPicker(false)}
        >
          <div className='asset-finding-picker'>
            <Button
              outline
              small
              onClick={() => {
                setShowPicker(false);
                setShowAddNew(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add a New Finding
            </Button>

            <h5 className='asset-finding-picker__heading'>
              Link an existing finding
            </h5>

            {loading ? (
              <p className='asset-section__empty'>Loading findings…</p>
            ) : unlinked.length === 0 ? (
              <p className='asset-section__empty'>
                No unlinked findings in this inspection.
              </p>
            ) : (
              <ul className='asset-finding-picker__list'>
                {unlinked.map((findng) => (
                  <li key={findng.id}>
                    <span>{findingLabel(findng)}</span>
                    <Button
                      outline
                      small
                      disabled={busyId === findng.id}
                      onClick={() => linkFinding(findng.id)}
                    >
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}

      {manageable && (
        <AddFindingModal
          show={showAddNew}
          hideModal={() => setShowAddNew(false)}
          inspectionId={inspectionId!}
          fixedAssetId={asset.id}
          defaultLocation={
            asset.latitude != null && asset.longitude != null
              ? `${asset.latitude}, ${asset.longitude}`
              : undefined
          }
          defaultDate={
            inspectionContext?.getDefaultFindingDate?.() || new Date()
          }
          onAddFinding={() => {
            setShowAddNew(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default AssetFindings;
